// ---------------------------------------------------------------------------
// HTTP client ของ PaymentBroker-Backend (เหมือน AITalk / LevelUpCRM)
//   response: { status: true|false, data, message }
//   token: localStorage["scp-acc-tk"] = btoa("Bearer " + token)
// ---------------------------------------------------------------------------
import { API_BASE_URL, TOKEN_KEY, PROFILE_KEY, AUTH_EVENT } from "./config";

const EXPIRED_MESSAGES = ["Token not found", "Token has expired"];

export class ApiError extends Error {
  constructor(message, payload) {
    super(message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    this.name = "ApiError";
    this.payload = payload;
  }
}

export const getRawToken = () => localStorage.getItem(TOKEN_KEY) || "";

/** "Bearer xxx" */
export const getAuthHeader = () => {
  const raw = getRawToken();
  if (!raw) return "";
  try {
    return atob(raw);
  } catch (e) {
    return "";
  }
};

/** token เปล่า (ไม่มีคำว่า Bearer) — ไม่มีคำว่า Bearer */
export const getAuthToken = () => getAuthHeader().replace(/^Bearer\s+/i, "");

export const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, btoa("Bearer " + token));
  else localStorage.removeItem(TOKEN_KEY);
};

export const getStoredProfile = () => {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const setStoredProfile = (profile) => {
  if (profile) localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  else localStorage.removeItem(PROFILE_KEY);
};

export const clearSession = () => {
  setToken(null);
  setStoredProfile(null);
  window.dispatchEvent(new Event(AUTH_EVENT));
};

const buildQuery = (params) => {
  if (!params) return "";
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (!entries.length) return "";
  return "?" + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
};

const readMessage = (payload) => {
  if (!payload) return "";
  if (typeof payload.message === "string") return payload.message;
  if (payload.message?.message) return payload.message.message;
  if (payload.error) return String(payload.error);
  return "";
};

const request = async (method, path, { params, body, signal } = {}) => {
  const url = path.includes("://") ? path : `${API_BASE_URL}${path}`.replace(/\s/g, "") + buildQuery(params);
  const authorization = getAuthHeader();

  let payload = null;
  try {
    const response = await fetch(url, {
      method,
      signal,
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
        ...(authorization ? { Authorization: authorization } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    payload = await response.json();
  } catch (e) {
    return { status: false, data: null, message: "เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ" };
  }

  const message = readMessage(payload);
  if (EXPIRED_MESSAGES.includes(message)) {
    clearSession();
    return { status: false, data: null, message, expired: true };
  }
  // สำเร็จต้องเป็น status === true เท่านั้น — error handler ของ Backend ส่ง status เป็นตัวเลข (เช่น 400) ซึ่ง JS ถือว่าจริง
  if (payload?.status === true) return payload;
  return { status: false, data: payload?.data ?? null, message: message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง" };
};

export const http = {
  get: (path, params, options) => request("GET", path, { ...options, params }),
  post: (path, body, options) => request("POST", path, { ...options, body }),
  put: (path, body, options) => request("PUT", path, { ...options, body }),
  delete: (path, options) => request("DELETE", path, options),
};
